import {
    type Action,
    type ActionExample,
    type IAgentRuntime,
    type Memory,
    type State,
    type HandlerCallback,
    elizaLogger,
    composePromptFromState,
    parseKeyValueXml,
    ModelType,
    type Content,
} from "@elizaos/core";
import { validateAvalancheConfig } from "../environment";
import { createMarketAndToken } from "../utils/tokenMill";

export interface TokenMillCreateContent extends Content {
    name: string;
    symbol: string;
}

function isTokenMillCreateContent(
    _runtime: IAgentRuntime,
    content: any
): content is TokenMillCreateContent {
    elizaLogger.debug("Content for create", content);
    return (
        typeof content.name === "string" && typeof content.symbol === "string"
    );
}

const transferTemplate = `Respond with an XML block containing only the extracted values. Use key-value pairs.

If the user did not provide enough details, respond with what you can. Name and Symbol are required.

Example response for a new token:
<response>
    <name>Test Token</name>
    <symbol>TEST</symbol>
</response>

## Recent Messages

{{recentMessages}}

Given the recent messages, extract the following information about the requested token creation:
- Name
- Symbol

Respond with an XML block containing only the extracted values.`;

export default {
    name: "CREATE_TOKEN",
    similes: [
        "LAUNCH_TOKEN",
        "NEW_TOKEN",
        "CREATE_MEMECOIN",
        "CREATE_MEME_TOKEN",
    ],
    validate: async (runtime: IAgentRuntime, _message: Memory) => {
        await validateAvalancheConfig(runtime);
        return true;
    },
    description:
        "MUST use this action if the user requests to create a new token, the request might be varied, but it will always be a token creation.",
    handler: async (
        runtime: IAgentRuntime,
        message: Memory,
        state: State,
        _options: { [key: string]: unknown },
        callback?: HandlerCallback
    ) => {
        elizaLogger.log("Starting CREATE_TOKEN handler...");

        // Initialize or update state
        let currentState = state;
        if (!currentState) {
            currentState = await runtime.composeState(message);
        } else {
            currentState = await runtime.composeState(message, [
                "RECENT_MESSAGES",
            ]);
        }

        // Compose prompt from state
        const prompt = composePromptFromState({
            state: currentState,
            template: transferTemplate,
        });

        // Generate content using the model
        const result = await runtime.useModel(ModelType.TEXT_SMALL, {
            prompt,
            stopSequences: [],
        });

        // Parse the result
        const content = parseKeyValueXml(result);

        elizaLogger.debug("Create content:", content);

        // Validate transfer content
        if (!isTokenMillCreateContent(runtime, content)) {
            elizaLogger.error("Invalid content for CREATE_TOKEN action.");
            callback?.({
                text: "Unable to process transfer request. Invalid content provided.",
                content: { error: "Invalid content" },
            });
            return false;
        }

        const { tx, baseToken, market } = await createMarketAndToken(
            runtime,
            content.name,
            content.symbol
        );
        callback?.({
            text: `Created token ${content.name} with symbol ${content.symbol}. CA: ${baseToken}`,
            content: { tx, baseToken, market },
        });
        return true;
    },
    examples: [
        [
            {
                user: "{{user1}}",
                content: {
                    text: "Create a new memecoin called 'Test Token' with the symbol 'TEST'",
                },
            },
            {
                name: "{{user2}}",
                content: {
                    action: "CREATE_TOKEN",
                    name: "Test Token",
                    symbol: "TEST",
                },
            },
        ],
        [
            {
                user: "{{user1}}",
                content: { text: "Create a token called news" },
            },
            {
                name: "{{user2}}",
                content: {
                    action: "CREATE_TOKEN",
                    name: "News Token",
                    symbol: "NEWS",
                },
            },
        ],
        [
            {
                user: "{{user1}}",
                content: { text: "Create a token" },
            },
            {
                name: "{{user2}}",
                content: {
                    action: "CREATE_TOKEN",
                    name: "Okay",
                    symbol: "OK",
                },
            },
        ],
    ] as ActionExample[][],
} as Action;
